import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/queries/keys';
import askHobbesHistoryService from '@/lib/api/askHobbesHistory';
import type {
  AskHobbesChatDetailResponse,
  AskHobbesChatListResponse,
  AskHobbesHistoryListResponse,
} from '@/lib/api/askHobbesHistory';

export function useAskHobbesChats(organizationId: string | undefined) {
  return useQuery<AskHobbesChatListResponse>({
    queryKey: queryKeys.askHobbesHistory.list(organizationId ?? ''),
    queryFn: async () => {
      const res = await askHobbesHistoryService.getChats(organizationId!);
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to fetch Ask Hobbes chats');
      }
      return res.data;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useAskHobbesChat(chatId: string | null | undefined) {
  return useQuery<AskHobbesChatDetailResponse>({
    queryKey: queryKeys.askHobbesHistory.chat(chatId ?? ''),
    queryFn: async () => {
      const res = await askHobbesHistoryService.getChat(chatId!);
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to fetch Ask Hobbes chat');
      }
      return res.data;
    },
    enabled: !!chatId,
    staleTime: 10_000,
  });
}

export function useRenameAskHobbesChat(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chatId, title }: { chatId: string; title: string }) => {
      const res = await askHobbesHistoryService.renameChat(chatId, title);
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to rename chat');
      }
      return res.data;
    },
    onSuccess: (chat) => {
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.askHobbesHistory.list(organizationId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.askHobbesHistory.chat(chat.id),
      });
    },
  });
}

export function useDeleteAskHobbesChat(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chatId: string) => {
      const res = await askHobbesHistoryService.deleteChat(chatId);
      if (!res.success) {
        throw new Error(res.error ?? 'Failed to delete chat');
      }
      return res.data;
    },
    onSuccess: () => {
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.askHobbesHistory.list(organizationId),
        });
      }
    },
  });
}

/**
 * React Query hook to fetch paginated Ask Hobbes conversation history.
 */
export function useAskHobbesHistory(organizationId: string | undefined) {
  return useQuery<AskHobbesHistoryListResponse>({
    queryKey: queryKeys.askHobbesHistory.list(organizationId ?? ''),
    queryFn: async () => {
      const res = await askHobbesHistoryService.getHistory(organizationId!);
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to fetch Ask Hobbes history');
      }
      return res.data;
    },
    enabled: !!organizationId,
    staleTime: 30_000,
  });
}

/**
 * Mutation hook to delete a single Ask Hobbes history entry.
 */
export function useDeleteAskHobbesEntry(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      const res = await askHobbesHistoryService.deleteEntry(entryId);
      if (!res.success) {
        throw new Error(res.error ?? 'Failed to delete history entry');
      }
      return res.data;
    },
    onSuccess: () => {
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.askHobbesHistory.list(organizationId),
        });
      }
    },
  });
}
